import React, { lazy, Suspense } from 'react';
import { Route, Switch, withRouter, Redirect, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Route Guards
import { PublicRoute } from './routeguards/PublicRoute';
import { PrivateRoute } from './routeguards/PrivateRoute';
import { CompanyRoute } from './routeguards/CompanyRoute.jsx';
import BillingAccessGate from './routeguards/BillingAccessGate';

// Providers
import { AuthProvider, useAuth } from './providers/AuthProvider';
import { SettingsProvider } from './context/SettingsContext';
import { FeatureProvider } from './context/FeatureContext';
import { CapabilityProvider } from './context/CapabilityContext';
import { InboxProvider } from './context/InboxContext';
// "What is the user looking at" — the typed task descriptor assistant requests carry
// (docs/TASK_CONTEXT_REGISTRY.md §7.2). A hint about the current selection, never a capability:
// the gateway validates it and the backend authorizes independently of it.
import { TaskDescriptorProvider } from './context/TaskContextDescriptor';

import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css";
import "./css/style.css";

// Components
import Index from "./jsx";
import SelectCompany from './jsx/pages/SelectCompany';
import CompanyClosures from './jsx/pages/CompanyClosures';
import McpConsent from './jsx/pages/McpConsent';
import McpAction from './jsx/pages/McpAction';
import ConnectedApps from './jsx/pages/ConnectedApps';
import NewCompany from './jsx/pages/NewCompany';
import CompanyPlanSelection from './jsx/pages/CompanyPlanSelection';
import AcceptInvitation from './jsx/pages/AcceptInvite';
import PublicQuoteView from './jsx/components/AppsMenu/Quotes/PublicQuoteView';
import { InvoiceSettingsProvider } from './context/InvoiceSettingsContext.js';
import { AccountingSettingsProvider } from './context/AccountingSettingsContext.js';
import { QuoteSettingsProvider } from "./context/QuoteSettingsContext";

// Lazy-loaded components
const SignUp = lazy(() => import('./jsx/pages/Registration'));
const ForgotPassword = lazy(() => import('./jsx/pages/ForgotPassword'));
const Login = lazy(() => import('./jsx/pages/Login'));
const Privacy = lazy(() => import('./jsx/pages/Privacy'));
// REMOVED: SignupCodesAdmin - moved to ledgerloop-admin app

const AuthenticatedApp = ({ children }) => {
  return (
    <SettingsProvider>
      <FeatureProvider>
        <CapabilityProvider>
          <BillingAccessGate>
            <Switch>
              <Route exact path="/:companyId/choose-plan" component={CompanyPlanSelection} />
              <Route render={() => <ProductProviders>{children}</ProductProviders>} />
            </Switch>
          </BillingAccessGate>
        </CapabilityProvider>
      </FeatureProvider>
    </SettingsProvider>
  );
};

// Product contexts make tenant data requests. Mount them only after the
// company has cleared the initial billing gate, alongside the product layout.
const ProductProviders = ({ children }) => (
  <InvoiceSettingsProvider>
    <QuoteSettingsProvider>
      <AccountingSettingsProvider>
        <InboxProvider>
          <TaskDescriptorProvider>
            {children}
          </TaskDescriptorProvider>
        </InboxProvider>
      </AccountingSettingsProvider>
    </QuoteSettingsProvider>
  </InvoiceSettingsProvider>
);

const MainAppComponent = () => (
  <AuthenticatedApp>
    <Index />
  </AuthenticatedApp>
);

const isPublicRoute = (pathname) => {
  const publicRoutes = [
    '/privacy',
    '/login',
    '/page-register',
    '/page-forgot-password'
  ];

  if (publicRoutes.includes(pathname)) {
    return true;
  }

  if (pathname.includes('/public/quote/view/') || pathname.includes('/invite')) {
    return true;
  }

  return false;
};

const AppContent = () => {
  const { isInitialized, isLoading } = useAuth();
  const location = useLocation();
  // Selectors read the canonical reducer keys: `isAuthenticated` and
  // `isCompanySelected` (Selector helpers also exist in
  // src/store/selectors/AuthSelectors.js — use those instead of inline reads
  // when adding new consumers). Pre-2026-05-14 this read
  // `state.auth.companyIsSelected` (note swapped `is`/`Is`), which was
  // always undefined → falsy → fallback redirect at line ~145 always fell
  // into "no company selected." Masked because <CompanyRoute path="/">
  // above this catch-all handles selection; flagged in 2026-05-14 review.
  const userIsAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const companyIsSelected = useSelector((state) => state.auth.isCompanySelected);

  // Show loading while authentication is being initialized
  if (!isInitialized || isLoading) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center" role="status" aria-label="Laddar">
        <div className="spinner-border text-primary" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="vh-100">
      <Suspense fallback={
        <div id="preloader" role="status" aria-label="Laddar">
          <div className="spinner-border text-primary" aria-hidden="true" />
        </div>
      }>
        <Switch>
          {/* Public Routes - No authentication required */}
          <PublicRoute
            path="/:companyId/public/quote/view/:token"
            component={PublicQuoteView}
          />
          <PublicRoute
            path="/:companyId/invite"
            component={AcceptInvitation}
          />

          <Route exact path="/privacy" component={Privacy} />
          {/* Authentication Routes */}
          <PublicRoute path="/login" component={Login} />
          <PublicRoute path="/page-register" component={SignUp} />
          <PublicRoute path="/forgot-password" component={ForgotPassword} />

          {/* Company Management Routes */}
          <PrivateRoute path="/create-company" component={NewCompany} />
          <PrivateRoute path="/select-company" component={SelectCompany} />
          <PrivateRoute exact path="/company-closures" component={CompanyClosures} />
          <Route exact path="/mcp/consent" component={McpConsent} />
          <Route exact path="/mcp/action" component={McpAction} />
          <PrivateRoute exact path="/connected-apps" component={ConnectedApps} />

          {/* Platform Admin Routes - REMOVED: moved to ledgerloop-admin app */}

          {/* Main Application Routes */}
          <CompanyRoute
            path="/"
            component={MainAppComponent}
          />

          {/* Default Redirects - Only if no routes matched */}
          <Route
            exact
            path="/"
            render={() => {
              if (!userIsAuthenticated) {
                return <Redirect to="/login" />;
              }
              if (!companyIsSelected) {
                return <Redirect to="/select-company" />;
              }
              // If user is authenticated and has company selected, redirect to dashboard
              const storedCompany = localStorage.getItem('selectedCompany');
              if (storedCompany) {
                const { companyId } = JSON.parse(storedCompany);
                return <Redirect to={`/${companyId}/dashboard`} />;
              }
              return <Redirect to="/select-company" />;
            }}
          />
        </Switch>
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default withRouter(App);
